<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/External_Components/Replacing_a_Part_List';
    let nextLink= '/Examples/Website_Integration';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="a-sorted-table-1"
  bind:intersecting={visibleById["a-sorted-table-1"]}
  onIntersectChange={(visible) => setVisible("a-sorted-table-1", visible)}
>A Sorted Table</SectionComponent>
<p>The native Freon <code>Table</code> takes a list of nodes of the same type and displays their properties in columns or rows. But what if you want something different? In this example we sort a schedule’s list of time slots by their <code>TimeStamp</code> (day/part) <em>before</em> displaying the schedule.</p>
<p>As a reminder, here are the AST definitions of <code>Schedule</code>, <code>Slot</code> and <code>TimeStamp</code>.</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase5/defs/main.ast#L10-L14

modelunit Schedule &#123;
    name: identifier;
    timeSlots: Slot[];
    file-extension = "scd";     // the file extension used by the parser
&#125;</code></pre>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase5/defs/main.ast#L33-L53

concept Slot &#123;
    time: TimeStamp;
    reference teacher: Person;
    reference room: Room;
    reference course: Course;
&#125;

limited TimeStamp &#123;
    day: number; // 1 = Monday, 2 = Tuesday, etc
    part: number; // 1 indicates morning, 2 indicates afternoon
    MondayMorning = &#123; day: 1, part: 1 &#125;
    TuesdayMorning = &#123; day: 2, part: 1 &#125;
    WednesdayMorning = &#123; day: 3, part: 1 &#125;
    ThursdayMorning = &#123; day: 4, part: 1 &#125;
    FridayMorning = &#123; day: 5, part: 1 &#125;
    MondayAfternoon = &#123; day: 1, part: 2 &#125;
    TuesdayAfternoon = &#123; day: 2, part: 2 &#125;
    WednesdayAfternoon = &#123; day: 3, part: 2 &#125;
    ThursdayAfternoon = &#123; day: 4, part: 2 &#125;
    FridayAfternoon = &#123; day: 5, part: 2 &#125;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="step-1-create-the-svelte-component-2"
  bind:intersecting={visibleById["step-1-create-the-svelte-component-2"]}
  onIntersectChange={(visible) => setVisible("step-1-create-the-svelte-component-2", visible)}
>Step 1: Create the Svelte Component</SectionComponent>
<p>To create a dynamic schedule table, we define a Svelte component named <code>Schedule.svelte</code>. This component handles sorting the time slots, displays the sorted grid, and lets the user add new slots.</p>
<h3 id="the-script-section-1">The Script Section</h3>
<p>The box type we use is a <code>PartListReplacerBox</code>. The four mandatory functions are similar to the ones in the <code>StaffAccordion</code> example, with one exception explained later. The key to sorting is the <code>initialize()</code> function, which processes the list of <code>timeSlots</code> and sorts them by <code>TimeStamp</code>. While sorting, we remember which child box is associated with which <code>Slot</code> in <code>slotToBoxMap</code> so we can render the correct child box.</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase5/src/external/Schedule.svelte#L56-L156

    TimeStamp.TuesdayAfternoon,
    TimeStamp.WednesdayAfternoon,
    TimeStamp.ThursdayAfternoon,
    TimeStamp.FridayAfternoon
];

function sortSlots(startVal: Slot[]) &#123;
    const newSlots: Slot[][] = []
    for (let i = 0; i &lt; 10 ; i++) &#123;
        newSlots[i] = [];
    &#125;
    (startVal).forEach((val, index) =&gt; &#123;
        // remember which box belongs to which slot
        slotToBoxMap.set(val, box.children[index]);
        switch (val.$time.day) &#123;
            case 1: &#123;
                switch (val.$time.part) &#123;
                    case 1: &#123; // Monday morning
                        newSlots[0].push(val);
                        break;
                    &#125;
                    case 2: &#123; // Monday afternoon
                        newSlots[5].push(val);
                        break;
                    &#125;
                    default: &#123;
                        newSlots[0].push(val);
                    &#125;
                &#125;
                break;
            &#125;
            case 2: &#123;
                switch (val.$time.part) &#123;
                    case 1: &#123; // Tuesday morning
                        newSlots[1].push(val);
                        break;
                    &#125;
                    case 2: &#123; // Tuesday afternoon
                        newSlots[6].push(val);
                        break;
                    &#125;
                    default: &#123;
                        newSlots[1].push(val);
                    &#125;
                &#125;
                break;
            &#125;
            case 3: &#123;
                switch (val.$time.part) &#123;
                    case 1: &#123; // Wednesday morning
                        newSlots[2].push(val);
                        break;
                    &#125;
                    case 2: &#123; // Wednesday afternoon
                        newSlots[7].push(val);
                        break;
                    &#125;
                    default: &#123;
                        newSlots[2].push(val);
                    &#125;
                &#125;
                break;
            &#125;
            case 4: &#123;
                switch (val.$time.part) &#123;
                    case 1: &#123; // Thursday morning
                        newSlots[3].push(val);
                        break;
                    &#125;
                    case 2: &#123; // Thursday afternoon
                        newSlots[8].push(val);
                        break;
                    &#125;
                    default: &#123;
                        newSlots[3].push(val);
                    &#125;
                &#125;
                break;
            &#125;
            case 5: &#123;
                switch (val.$time.part) &#123;
                    case 1: &#123; // Friday morning
                        newSlots[4].push(val);
                        break;
                    &#125;
                    case 2: &#123; // Friday afternoon
                        newSlots[9].push(val);
                        break;
                    &#125;
                    default: &#123;
                        newSlots[4].push(val);
                    &#125;
                &#125;
                break;
            &#125;
        &#125;
    &#125;)
    sortedSlots = newSlots
&#125;

/* Sort the list of slots based on the time */</code></pre>
<p>The function that adds a new <code>Slot</code> takes a <code>TimeStamp</code> parameter, allowing us to create a new slot for the specified time. We didn’t include a “remove slot” function here; it would be similar to the one in <code>StaffAccordion</code>.</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase5/src/external/Schedule.svelte#L158-L165








</code></pre>
<p>Then there are two variables that make life easier in the HTML part.</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase5/src/external/Schedule.svelte#L40-L54

    for (let i = 0; i &lt; 10; i++) &#123;
        slots[i] = [];
    &#125;
    return slots;
&#125;

let dayTitle: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday' ];

// variables for creating a new slot
let timeStamps: TimeStamp[] = [
    TimeStamp.MondayMorning,
    TimeStamp.TuesdayMorning,
    TimeStamp.WednesdayMorning,
    TimeStamp.ThursdayMorning,
    TimeStamp.FridayMorning,</code></pre>
<h3 id="the-html-section-2">The HTML Section</h3>
<p>We couldn’t use the <code>Table</code> component from the library because it clips its content (including dropdowns). With a bit of CSS we recreated a table in plain HTML.</p>
<p>First, the headers: each holds a day name. Because we also display row headers (<code>Morning</code> and <code>Afternoon</code>), the first header cell is empty.</p>
<pre class="language-svelte"><code class="language-svelte">// CourseSchedule/phase5/src/external/Schedule.svelte#L193-L203

&lt;div class="demo-table-container"&gt;
    &lt;table class="demo-table"&gt;
        &lt;thead&gt;
        &lt;tr class="demo-header-row"&gt;
            &lt;th class="demo-header-cell"&gt;&lt;/th&gt;
            &#123;#each dayTitle as title&#125;
                &lt;th class="demo-header-cell"&gt;&#123;title&#125;&lt;/th&gt;
            &#123;/each&#125;
        &lt;/tr&gt;
        &lt;/thead&gt;
        &lt;tbody&gt;</code></pre>
<p>Next, we create two rows—one for mornings and one for afternoons.<br>
For the morning row: the first cell is the row header (<code>Morning</code>). We then loop over <code>sortedSlots</code> but only take the first five entries (the mornings). Each morning can contain a <em>list</em> of slots. For each slot we resolve its child box via <code>slotToBoxMap</code> and render it with Freon’s <code>&lt;RenderComponent&gt;</code>. The afternoon row mirrors this but uses the last five entries.</p>
<pre class="language-svelte"><code class="language-svelte">// CourseSchedule/phase5/src/external/Schedule.svelte#L204-L225

&lt;tr class="demo-row"&gt;
    &lt;td class="demo-header-cell"&gt;Morning&lt;/td&gt;
    &#123;#each sortedSlots as slots, index&#125;
        &#123;#if index &lt; 5&#125;
            &#123;#if slots.length &gt; 0&#125;
                &lt;td class="demo-cell"&gt;
                    &lt;div class="demo-cell-content"&gt;
                    &#123;#each slots as slot&#125;
                        &lt;div class="demo-slot-render"&gt;
                        &lt;RenderComponent box=&#123;findBoxForSlot(slot)&#125; editor=&#123;editor&#125; /&gt;
                        &lt;/div&gt;
                    &#123;/each&#125;
                    &lt;/div&gt;
                &lt;/td&gt;
            &#123;:else&#125;
                &lt;td class="demo-cell"&gt;
                    &lt;div class="demo-slot-render"&gt;NONE&lt;/div&gt;
                &lt;/td&gt;
            &#123;/if&#125;
        &#123;/if&#125;
    &#123;/each&#125;
&lt;/tr&gt;</code></pre>
<p>Between the two time rows we add button rows so the user can create a slot for a specific time. Again, the first cell is empty (row header column).
We loop over <code>timeStamps</code> and pass each value to <code>addSlot</code>.</p>
<pre class="language-svelte"><code class="language-svelte">// CourseSchedule/phase5/src/external/Schedule.svelte#L226-L237

&lt;tr&gt;
    &lt;td class="demo-btn-cell"&gt;&lt;/td&gt;
    &#123;#each timeStamps as stamp, index&#125;
        &#123;#if index &lt; 5&#125;
            &lt;td class="demo-btn-cell"&gt;
                &lt;Button tabindex=&#123;-1&#125; id="add-button" class="&#123;buttonCls&#125; &#123;colorCls&#125; " name="ToastOpen" onclick=&#123;() =&gt; addSlot(stamp)&#125;&gt;
                    &lt;UserAddOutline class="&#123;iconCls&#125;" /&gt;
                &lt;/Button&gt;
            &lt;/td&gt;
        &#123;/if&#125;
    &#123;/each&#125;
&lt;/tr&gt;</code></pre>
<p>The complete Svelte component is at the bottom of this page.</p>
<SectionComponent tag="h2" 
  id="step-2-include-in-the-projection-3"
  bind:intersecting={visibleById["step-2-include-in-the-projection-3"]}
  onIntersectChange={(visible) => setVisible("step-2-include-in-the-projection-3", visible)}
>Step 2: Include in the Projection</SectionComponent>
<p>We include the new component in the projection with <code>replace=Schedule</code>. Because the table already conveys each slot’s time,
we hide <code>\$&#123;self.time&#125;</code> in the <code>Slot</code> projection.</p>
<pre class="language-freon"><code class="language-freon">// CourseSchedule/phase5/defs/externals.edit#L20-L31

Schedule &#123;[
Schedule $&#123;self.name&#125;

$&#123;self.timeSlots replace=Schedule&#125;

]&#125;

Slot &#123;[
    Teacher: $&#123;self.teacher&#125;
    Room:    $&#123;self.room&#125;
    Course:  $&#123;self.course&#125;
]&#125;</code></pre>
<p>By now you know the small amount of admin needed to register an external component, so we won’t repeat it here.</p>
<SectionComponent tag="h2" 
  id="final-result-4"
  bind:intersecting={visibleById["final-result-4"]}
  onIntersectChange={(visible) => setVisible("final-result-4", visible)}
>Final Result</SectionComponent>
<p>When all is done, the editor should look like this. Neat, right?</p>
<Figure
imageName={'examples/CourseSchedule/Screenshot-step5.png'}
caption={'Editor with sorted table showing Slots'}
figureNumber={1}
/>
<SectionComponent tag="h2" 
  id="conclusion-5"
  bind:intersecting={visibleById["conclusion-5"]}
  onIntersectChange={(visible) => setVisible("conclusion-5", visible)}
>Conclusion</SectionComponent>
<p>After following these steps, you’ll have a Svelte component that displays a sorted table of time slots. This approach adapts well
to any case where you want to preprocess and present structured data in a table. The editor shows the schedule neatly sorted by
day and time, and users can interactively add slots.</p>
<p>This extended example demonstrates how custom Svelte components in the Freon editor unlock many design and UX possibilities.
Once you’re comfortable with this pattern, you can reuse it with any of the <a
  href="/Documentation/Under_the_Hood/Editor_Framework/External_Component_Box_Types"
>External Component Box Types</a>.</p>
<p>Since external components are still experimental, we’re eager to learn how you plan to use them. If you decide to incorporate
them, please reach out to the Freon team at <a
  href="mailto:info@openmodeling.nl"
>info@openmodeling.nl</a> or via
GitHub — we’re happy to help.</p>
<SectionComponent tag="h2" 
  id="the-complete-svelte-component-6"
  bind:intersecting={visibleById["the-complete-svelte-component-6"]}
  onIntersectChange={(visible) => setVisible("the-complete-svelte-component-6", visible)}
>The Complete Svelte Component</SectionComponent>
<p>For reference, here is the full implementation of the <code>Schedule.svelte</code> component:</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase5/src/external/Schedule.svelte

&lt;script lang="ts"&gt;
    import &#123;
        Box,
        PartListReplacerBox,
        type FreNode,
        FreNodeReference,
        AST, isNullOrUndefined, LabelBox, notNullOrUndefined
    &#125; from "@freon4dsl/core"
    import &#123;type FreComponentProps, RenderComponent&#125; from "@freon4dsl/core-svelte";
    import &#123;Slot, TimeStamp&#125; from "../freon/index.js";
    import &#123; UserAddOutline &#125; from 'flowbite-svelte-icons';
    import &#123; Button &#125; from 'flowbite-svelte';

    // This component replaces the component for "timeSlots: Slot[];" from model unit "Schedule".
    // This property is a parts list, therefore the external box to use is an PartListReplacerBox.
    // Props
    let &#123; editor, box &#125;: FreComponentProps&lt;PartListReplacerBox&gt; = $props();

    // The following three functions need to be included for the editor to function properly.
    // Please, set the focus to the first editable/selectable element in this component.
    async function setFocus(): Promise&lt;void&gt; &#123;
    &#125;
    const refresh = (why?: string): void =&gt; &#123;
        // do whatever needs to be done to refresh the elements that show information from the model
        initialize();
    &#125;;
    $effect(() =&gt; &#123;
        initialize();
        box.setFocus = setFocus;
        box.refreshComponent = refresh;
    &#125;);

    // --------------------------- //
    let slotToBoxMap: Map&lt;Slot, Box&gt; = new Map&lt;Slot, Box&gt;();
    // an array of 10 positions, making use of the 10 different timeSlots that are available
    let sortedSlots: Slot[][] = $state(initSortedSlots());

    function initSortedSlots(): Slot[][] &#123;
        let slots = [];
        for (let i = 0; i &lt; 10; i++) &#123;
            slots[i] = [];
        &#125;
        return slots;
    &#125;

    let dayTitle: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday' ];

    // variables for creating a new slot
    let timeStamps: TimeStamp[] = [
        TimeStamp.MondayMorning,
        TimeStamp.TuesdayMorning,
        TimeStamp.WednesdayMorning,
        TimeStamp.ThursdayMorning,
        TimeStamp.FridayMorning,
        TimeStamp.MondayAfternoon,
        TimeStamp.TuesdayAfternoon,
        TimeStamp.WednesdayAfternoon,
        TimeStamp.ThursdayAfternoon,
        TimeStamp.FridayAfternoon
    ];

    function sortSlots(startVal: Slot[]) &#123;
        const newSlots: Slot[][] = []
        for (let i = 0; i &lt; 10 ; i++) &#123;
            newSlots[i] = [];
        &#125;
        (startVal).forEach((val, index) =&gt; &#123;
            // remember which box belongs to which slot
            slotToBoxMap.set(val, box.children[index]);
            switch (val.$time.day) &#123;
                case 1: &#123;
                    switch (val.$time.part) &#123;
                        case 1: &#123; // Monday morning
                            newSlots[0].push(val);
                            break;
                        &#125;
                        case 2: &#123; // Monday afternoon
                            newSlots[5].push(val);
                            break;
                        &#125;
                        default: &#123;
                            newSlots[0].push(val);
                        &#125;
                    &#125;
                    break;
                &#125;
                case 2: &#123;
                    switch (val.$time.part) &#123;
                        case 1: &#123; // Tuesday morning
                            newSlots[1].push(val);
                            break;
                        &#125;
                        case 2: &#123; // Tuesday afternoon
                            newSlots[6].push(val);
                            break;
                        &#125;
                        default: &#123;
                            newSlots[1].push(val);
                        &#125;
                    &#125;
                    break;
                &#125;
                case 3: &#123;
                    switch (val.$time.part) &#123;
                        case 1: &#123; // Wednesday morning
                            newSlots[2].push(val);
                            break;
                        &#125;
                        case 2: &#123; // Wednesday afternoon
                            newSlots[7].push(val);
                            break;
                        &#125;
                        default: &#123;
                            newSlots[2].push(val);
                        &#125;
                    &#125;
                    break;
                &#125;
                case 4: &#123;
                    switch (val.$time.part) &#123;
                        case 1: &#123; // Thursday morning
                            newSlots[3].push(val);
                            break;
                        &#125;
                        case 2: &#123; // Thursday afternoon
                            newSlots[8].push(val);
                            break;
                        &#125;
                        default: &#123;
                            newSlots[3].push(val);
                        &#125;
                    &#125;
                    break;
                &#125;
                case 5: &#123;
                    switch (val.$time.part) &#123;
                        case 1: &#123; // Friday morning
                            newSlots[4].push(val);
                            break;
                        &#125;
                        case 2: &#123; // Friday afternoon
                            newSlots[9].push(val);
                            break;
                        &#125;
                        default: &#123;
                            newSlots[4].push(val);
                        &#125;
                    &#125;
                    break;
                &#125;
            &#125;
        &#125;)
        sortedSlots = newSlots
    &#125;

    /* Sort the list of slots based on the time */
    function initialize() &#123;
        let startVal: FreNode[] | undefined = box.getPropertyValue();
        if (notNullOrUndefined(startVal) &amp;&amp; box.getPropertyType() === "Slot") &#123;
            // cast the startVal to the expected type, in this case "Slot[]".
            // sort the slots based on the time and remember which box belongs to which slot
            sortSlots(startVal as Slot[]);
        &#125;
    &#125;

    const addSlot = (timeStamp: TimeStamp) =&gt; &#123;
        // Note that you need to put any changes to the actual model in a 'AST.change' or 'AST.changeNamed',
        // because all elements in the model are reactive using mobx.
        AST.change(() =&gt; &#123;
            let newSlot: Slot = Slot.create(&#123;time: FreNodeReference.create&lt;TimeStamp&gt;(timeStamp, "TimeStamp")&#125;);
            box.getPropertyValue().push(newSlot);
        &#125;);
    &#125;

    const findBoxForSlot = (slot: Slot): Box =&gt; &#123;
        let xx = slotToBoxMap.get(slot);
        if (!isNullOrUndefined(xx)) &#123;
            return xx;
        &#125; else &#123;
            return new LabelBox(box.node, 'no-role', () =&gt; &#123; return 'No box found'&#125;);
        &#125;
    &#125;
    initialize();
    const colorCls: string = 'text-light-base-50 dark:text-dark-base-900 ';
    const buttonCls: string =
      'bg-light-base-600 					dark:bg-dark-base-200 ' +
      'hover:bg-light-base-900 		dark:hover:bg-dark-base-50 ' +
      'border-light-base-100 			dark:border-dark-base-800 ';
    const iconCls: string = 'ms-0 inline h-6 w-6';
&lt;/script&gt;


&lt;div class="demo-table-container"&gt;
    &lt;table class="demo-table"&gt;
        &lt;thead&gt;
        &lt;tr class="demo-header-row"&gt;
            &lt;th class="demo-header-cell"&gt;&lt;/th&gt;
            &#123;#each dayTitle as title&#125;
                &lt;th class="demo-header-cell"&gt;&#123;title&#125;&lt;/th&gt;
            &#123;/each&#125;
        &lt;/tr&gt;
        &lt;/thead&gt;
        &lt;tbody&gt;
        &lt;tr class="demo-row"&gt;
            &lt;td class="demo-header-cell"&gt;Morning&lt;/td&gt;
            &#123;#each sortedSlots as slots, index&#125;
                &#123;#if index &lt; 5&#125;
                    &#123;#if slots.length &gt; 0&#125;
                        &lt;td class="demo-cell"&gt;
                            &lt;div class="demo-cell-content"&gt;
                            &#123;#each slots as slot&#125;
                                &lt;div class="demo-slot-render"&gt;
                                &lt;RenderComponent box=&#123;findBoxForSlot(slot)&#125; editor=&#123;editor&#125; /&gt;
                                &lt;/div&gt;
                            &#123;/each&#125;
                            &lt;/div&gt;
                        &lt;/td&gt;
                    &#123;:else&#125;
                        &lt;td class="demo-cell"&gt;
                            &lt;div class="demo-slot-render"&gt;NONE&lt;/div&gt;
                        &lt;/td&gt;
                    &#123;/if&#125;
                &#123;/if&#125;
            &#123;/each&#125;
        &lt;/tr&gt;
        &lt;tr&gt;
            &lt;td class="demo-btn-cell"&gt;&lt;/td&gt;
            &#123;#each timeStamps as stamp, index&#125;
                &#123;#if index &lt; 5&#125;
                    &lt;td class="demo-btn-cell"&gt;
                        &lt;Button tabindex=&#123;-1&#125; id="add-button" class="&#123;buttonCls&#125; &#123;colorCls&#125; " name="ToastOpen" onclick=&#123;() =&gt; addSlot(stamp)&#125;&gt;
                            &lt;UserAddOutline class="&#123;iconCls&#125;" /&gt;
                        &lt;/Button&gt;
                    &lt;/td&gt;
                &#123;/if&#125;
            &#123;/each&#125;
        &lt;/tr&gt;
        &lt;tr&gt;
            &lt;td class="demo-header-cell"&gt;Afternoon&lt;/td&gt;
            &#123;#each sortedSlots as slots, index&#125;
                &#123;#if index &gt;= 5&#125;
                    &#123;#if slots.length &gt; 0&#125;
                        &lt;td class="demo-cell"&gt;
                            &#123;#each slots as slot&#125;
                                &lt;div class="demo-slot-render"&gt;
                                &lt;RenderComponent box=&#123;findBoxForSlot(slot)&#125; editor=&#123;editor&#125; /&gt;
                                &lt;/div&gt;
                            &#123;/each&#125;
                        &lt;/td&gt;
                    &#123;:else&#125;
                        &lt;td class="demo-cell"&gt;
                            &lt;div class="demo-slot-render"&gt;
                                NONE
                            &lt;/div&gt;
                        &lt;/td&gt;
                    &#123;/if&#125;
                &#123;/if&#125;
            &#123;/each&#125;
        &lt;/tr&gt;
        &lt;tr&gt;
            &lt;td class="demo-btn-cell"&gt;&lt;/td&gt;
            &#123;#each timeStamps as stamp, index&#125;
                &#123;#if index &gt;= 5&#125;
                    &lt;td class="demo-btn-cell"&gt;
                        &lt;Button tabindex=&#123;-1&#125; id="add-button" class="&#123;buttonCls&#125; &#123;colorCls&#125; " name="ToastOpen" onclick=&#123;() =&gt; addSlot(stamp)&#125;&gt;
                            &lt;UserAddOutline class="&#123;iconCls&#125;" /&gt;
                        &lt;/Button&gt;
                    &lt;/td&gt;
                &#123;/if&#125;
            &#123;/each&#125;
        &lt;/tr&gt;
        &lt;/tbody&gt;
    &lt;/table&gt;
&lt;/div&gt;


&lt;style&gt;
    .demo-table-container &#123;
        background-color:#fff;
        color: rgba(0, 0, 0, 0.87);
        max-width: 100%;
        border-radius:4px;
        border-width:1px;
        border-style:solid;
        border-color:rgba(0,0,0,.12);
        display:inline-flex;
        flex-direction:column;
        box-sizing:border-box;
        position:relative;
    &#125;
    .demo-table &#123;
        min-width:100%;
        border:0;
        white-space:nowrap;
        border-spacing:0;
        table-layout:fixed;
    &#125;
    .demo-cell &#123;
        height: 200px;
        border-right-width: 1px;
        border-right-style: solid;
        border-right-color: rgba(0,0,0,.12);
    &#125;
    .demo-slot-render &#123;
        margin: 0 4px 20px 4px;
    &#125;
    .demo-header-row &#123;
        height: 56px;
    &#125;
    .demo-header-cell &#123;
        font-size:0.875rem;
        line-height:1.375rem;
        font-weight:bolder;
        box-sizing:border-box;
        text-align:left;
        padding: 0 16px 0 16px;
        border-right-width: 1px;
        border-right-style: solid;
        border-right-color: rgba(0,0,0,.12);
        background-color: var(--mdc-theme-surface, #fff);
    &#125;
    .demo-cell-content &#123;
        justify-content: space-between;
        flex-direction: column;
        display: flex;
    &#125;
    .demo-btn-cell &#123;
        border-bottom-width: 1px;
        border-bottom-style: solid;
        border-bottom-color: rgba(0,0,0,.12);
        border-right-width: 1px;
        border-right-style: solid;
        border-right-color: rgba(0,0,0,.12);
        justify-content: space-between;
    &#125;
&lt;/style&gt;
</code></pre>


<PrevNextSection {prevLink} {nextLink} />